import { CatalogItemProductType } from '@square/dashboard-protos/models/squareup/connect/v2/catalog/resources/enums';
import { CATALOG_ITEM_PRODUCT_TYPE } from '@square/dashboard-items-data/clients/catalog-connect-v2';
import NumericEnum from '@square/dashboard-utilities/utils/numeric-enum';

/**
 * ItemTypes is union type of possible Item Types.
 * Use it both in v3 and connect/v2 context as a type definition
 */

/**
 * ItemType is a numeric enum of item types. Use it when you need to send item type to v3 backend
 * because items_v3 APIs expect item type in numeric form.
 * Even though ItemType is used in the items_v3 context, we are generating it from the connect/v2 proto enum.
 * This is because both enum sets should be equivalent.
 */
export const ItemType = NumericEnum.createFromObject(CatalogItemProductType);

/**
 * ItemTypeConnect is POJO version of ItemType. Hence, it doesn't have ItemType's serialize/deserialize methods.
 * Use it when you need to send item type to connect_v2 backend because connect_v2 APIs expect item type in string form
 */
export const ItemTypeConnect = {};
Object.keys(CatalogItemProductType).forEach(key => {
  const _key = key;
  ItemTypeConnect[_key] = _key;
});
export const AllowedItemTypes = Object.freeze([ItemType.FOOD_AND_BEV, ItemType.REGULAR, ItemType.EVENT, ItemType.DIGITAL, ItemType.DONATION]);
export const AllowedItemTypesWithMembership = Object.freeze([...AllowedItemTypes, ItemType.LEGACY_SQUARE_ONLINE_SERVICE, ItemType.LEGACY_SQUARE_ONLINE_MEMBERSHIP]);
export const AppointmentsItemTypes = Object.freeze([ItemType.APPOINTMENTS_SERVICE]);

// returns array of NumericEnums which get serialized into the integer ItemType enum values [0, 6, ...]
// used wherever we call this.store.query('item')
export const ItemTypesForFiltering_V3 = Object.freeze([...AllowedItemTypesWithMembership]);
export const ItemTypesForFilteringWithAppointments_V3 = Object.freeze([...ItemTypesForFiltering_V3, ItemType.APPOINTMENTS_SERVICE]);

// returns array of Enums which get serialized into the string ItemType enum keys ['REGULAR', 'FOOD_AND_BEV', ...]
// used wherever we call this.store.query('connect/item')
export const ItemTypesForFiltering_ConnectV2 = Object.freeze([CATALOG_ITEM_PRODUCT_TYPE.FOOD_AND_BEV, CATALOG_ITEM_PRODUCT_TYPE.REGULAR, CATALOG_ITEM_PRODUCT_TYPE.EVENT, CATALOG_ITEM_PRODUCT_TYPE.DIGITAL, CATALOG_ITEM_PRODUCT_TYPE.DONATION, CATALOG_ITEM_PRODUCT_TYPE.LEGACY_SQUARE_ONLINE_SERVICE, CATALOG_ITEM_PRODUCT_TYPE.LEGACY_SQUARE_ONLINE_MEMBERSHIP]);
export const ItemTypesForFilteringWithAppointments_ConnectV2 = Object.freeze([...ItemTypesForFiltering_ConnectV2, CATALOG_ITEM_PRODUCT_TYPE.APPOINTMENTS_SERVICE]);

// returns array of ints with the enum numeric vals [0, 6, 7, 8, 9, 15, 12]
export const ItemTypesForFiltering_Numeric = Object.freeze([CatalogItemProductType.REGULAR, CatalogItemProductType.FOOD_AND_BEV, CatalogItemProductType.EVENT, CatalogItemProductType.DIGITAL, CatalogItemProductType.DONATION, CatalogItemProductType.LEGACY_SQUARE_ONLINE_MEMBERSHIP, CatalogItemProductType.LEGACY_SQUARE_ONLINE_SERVICE]);

// returns array of strings with the enum numeric vals ["0", "6", "7", "8", "9", "15", "12"]
// used in for v3 object query when using set_query_values
export const ItemTypesForFiltering_NumericStrings = Object.freeze(ItemTypesForFiltering_Numeric.map(v => v.toString()));

// Food and Beverage related
// ------------------------------------
const IngredientsI18nPrefix = 'items.type.nutritionalInfo.ingredients';
// while we do have a connect/v2 ENUM proto for the standard strings, we're treating this as a string because we can also have custom values
export const FoodAndBevIngredientsI18nMap = {
  STANDARD_CELERY: `${IngredientsI18nPrefix}.celery`,
  STANDARD_CRUSTACEANS: `${IngredientsI18nPrefix}.crustaceans`,
  STANDARD_EGGS: `${IngredientsI18nPrefix}.eggs`,
  STANDARD_FISH: `${IngredientsI18nPrefix}.fish`,
  STANDARD_GLUTEN: `${IngredientsI18nPrefix}.gluten`,
  STANDARD_LUPIN: `${IngredientsI18nPrefix}.lupin`,
  STANDARD_MILK: `${IngredientsI18nPrefix}.milk`,
  STANDARD_MOLLUSCS: `${IngredientsI18nPrefix}.molluscs`,
  STANDARD_MUSTARD: `${IngredientsI18nPrefix}.mustard`,
  STANDARD_PEANUTS: `${IngredientsI18nPrefix}.peanuts`,
  STANDARD_SESAME: `${IngredientsI18nPrefix}.sesame`,
  STANDARD_SOY: `${IngredientsI18nPrefix}.soy`,
  STANDARD_SULPHITES: `${IngredientsI18nPrefix}.sulphites`,
  STANDARD_TREE_NUTS: `${IngredientsI18nPrefix}.treeNuts`
};
export const ConnectV2FoodAndBevIngredientsI18nMap = {
  CELERY: `${IngredientsI18nPrefix}.celery`,
  CRUSTACEANS: `${IngredientsI18nPrefix}.crustaceans`,
  EGGS: `${IngredientsI18nPrefix}.eggs`,
  FISH: `${IngredientsI18nPrefix}.fish`,
  GLUTEN: `${IngredientsI18nPrefix}.gluten`,
  LUPIN: `${IngredientsI18nPrefix}.lupin`,
  MILK: `${IngredientsI18nPrefix}.milk`,
  MOLLUSCS: `${IngredientsI18nPrefix}.molluscs`,
  MUSTARD: `${IngredientsI18nPrefix}.mustard`,
  PEANUTS: `${IngredientsI18nPrefix}.peanuts`,
  SESAME: `${IngredientsI18nPrefix}.sesame`,
  SOY: `${IngredientsI18nPrefix}.soy`,
  SULPHITES: `${IngredientsI18nPrefix}.sulphites`,
  TREE_NUTS: `${IngredientsI18nPrefix}.treeNuts`
};
const DietaryPreferencesI18nPrefix = 'items.type.nutritionalInfo.dietaryPreferences';
export const FoodAndBevDietaryPreferencesI18nMap = {
  STANDARD_DAIRY_FREE: `${DietaryPreferencesI18nPrefix}.dairyFree`,
  STANDARD_GLUTEN_FREE: `${DietaryPreferencesI18nPrefix}.glutenFree`,
  STANDARD_HALAL: `${DietaryPreferencesI18nPrefix}.halal`,
  STANDARD_KOSHER: `${DietaryPreferencesI18nPrefix}.kosher`,
  STANDARD_NUT_FREE: `${DietaryPreferencesI18nPrefix}.nutFree`,
  STANDARD_VEGAN: `${DietaryPreferencesI18nPrefix}.vegan`,
  STANDARD_VEGETARIAN: `${DietaryPreferencesI18nPrefix}.vegetarian`
};
export const ConnectV2FoodAndBevDietaryPreferencesI18nMap = {
  DAIRY_FREE: `${DietaryPreferencesI18nPrefix}.dairyFree`,
  GLUTEN_FREE: `${DietaryPreferencesI18nPrefix}.glutenFree`,
  HALAL: `${DietaryPreferencesI18nPrefix}.halal`,
  KOSHER: `${DietaryPreferencesI18nPrefix}.kosher`,
  NUT_FREE: `${DietaryPreferencesI18nPrefix}.nutFree`,
  VEGAN: `${DietaryPreferencesI18nPrefix}.vegan`,
  VEGETARIAN: `${DietaryPreferencesI18nPrefix}.vegetarian`
};
export const itemTypesWithWeightField = new Set([ItemType.REGULAR, ItemType.FOOD_AND_BEV, ItemType.COMBO]);